import {React, styled} from 'common'
import {
    Alert,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Divider,
    Stack,
    Typography,
} from '@mui/material'
import {Page, PageContent} from 'components/Page'
import {useGetUserQuery} from 'store/Store'
import {useNavigate, useParams} from 'react-router-dom'
import moment from 'moment'

const USER_TYPE_LABELS = {
    elder: 'זקן',
    volunteer: 'מתנדב',
    specialist: 'מוקדן',
    social_worker: 'עו"ס',
    relative: 'קרוב משפחה',
    admin: 'מנהל מערכת',
}

const ROLE_LABELS = {
    OWNER: 'בעלים',
    ADMIN: 'מנהל',
    USER: 'משתמש',
}

const STATUS_LABELS = {
    registered: 'רשום',
    unverified: 'מחכה לאימות',
    pending_support: 'צריך סיוע',
    suspended: 'מושעה',
    verified: 'מאומת',
}

const formatValue = (value) => {
    if (value === undefined || value === null || value === '') return '—'
    if (typeof value === 'boolean') return value ? 'כן' : 'לא'
    return value
}

const formatDate = (date) => {
    if (!date) return '—'
    return moment(date).isValid()
        ? moment(date).format('DD/MM/YYYY HH:mm')
        : '—'
}

const formatAddress = (address) => {
    if (!address) return '—'

    return [
        address.street,
        address.streetNumber,
        address.apartment && `דירה ${address.apartment}`,
        address.floor && `קומה ${address.floor}`,
        address.city,
    ]
        .filter(Boolean)
        .join(', ')
}

const getDisplayName = (user) =>
    [user?.firstName, user?.lastName].filter(Boolean).join(' ') ||
    'משתמש ללא שם'

const getUserTypeLabel = (type) => USER_TYPE_LABELS[type] || type || '—'

const getStatusLabel = (status) => STATUS_LABELS[status] || status || '—'

const getAccountName = (userAccount) => {
    const account = userAccount?.account
    if (!account) return '—'
    if (typeof account === 'string') return account
    return account.name || account._id || '—'
}

const getAccountRole = (userAccount) =>
    ROLE_LABELS[userAccount?.role] || userAccount?.role || '—'

const isAdminUser = (user) =>
    user?.isSuper ||
    user?.role === 'ADMIN' ||
    user?.role === 'OWNER' ||
    user?.userAccounts?.some((userAccount) =>
        ['ADMIN', 'OWNER'].includes(userAccount?.role)
    )

const DetailRow = ({label, value}) => (
    <Row>
        <Label>{label}</Label>
        <Value>{formatValue(value)}</Value>
    </Row>
)

const Section = ({title, children}) => (
    <Card>
        <CardContent>
            <Stack spacing={2}>
                <Typography variant="h6">{title}</Typography>
                <Divider />
                <DetailsGrid>{children}</DetailsGrid>
            </Stack>
        </CardContent>
    </Card>
)

const UserDetailsContent = ({user}) => {
    const navigate = useNavigate()

    return (
        <Stack spacing={3}>
            <HeaderCard>
                <Stack spacing={2}>
                    <Stack
                        direction={{xs: 'column', sm: 'row'}}
                        justifyContent="space-between"
                        alignItems={{xs: 'flex-start', sm: 'center'}}
                        spacing={2}>
                        <Stack spacing={1}>
                            <Typography variant="h4">
                                {getDisplayName(user)}
                            </Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap">
                                <Chip label={getUserTypeLabel(user.type)} />
                                <Chip
                                    color={
                                        isAdminUser(user)
                                            ? 'primary'
                                            : 'default'
                                    }
                                    label={
                                        isAdminUser(user)
                                            ? 'משתמש מנהל'
                                            : 'לא מנהל'
                                    }
                                />
                                <Chip label={getStatusLabel(user.status)} />
                            </Stack>
                        </Stack>
                        <Stack direction="row" spacing={1}>
                            <Button
                                variant="outlined"
                                onClick={() => navigate('/users')}>
                                חזרה למשתמשים
                            </Button>
                            {user.type === 'volunteer' && (
                                <Button
                                    variant="contained"
                                    onClick={() =>
                                        navigate(`/user/${user._id}/chats`)
                                    }>
                                    הודעות
                                </Button>
                            )}
                        </Stack>
                    </Stack>
                </Stack>
            </HeaderCard>

            <Section title="פרטים אישיים">
                <DetailRow label="MongoDB ID" value={user._id} />
                <DetailRow
                    label="סוג משתמש"
                    value={getUserTypeLabel(user.type)}
                />
                <DetailRow label="שם פרטי" value={user.firstName} />
                <DetailRow label="שם משפחה" value={user.lastName} />
                <DetailRow label="טלפון" value={user.phone} />
                <DetailRow label="טלפון נוסף" value={user.additionalPhone} />
                <DetailRow label="אימייל" value={user.email} />
                <DetailRow label="תעודת זהות" value={user.nationalId} />
                <DetailRow label="סטטוס" value={getStatusLabel(user.status)} />
                <DetailRow
                    label="תפקיד כללי"
                    value={ROLE_LABELS[user.role] || user.role}
                />
                <DetailRow label="מנהל מערכת" value={isAdminUser(user)} />
                <DetailRow label="HubSpot ID" value={user.hubspotId} />
            </Section>

            <Section title="כתובות וארגון">
                <DetailRow
                    label="כתובת ראשית"
                    value={formatAddress(user.address)}
                />
                <DetailRow
                    label="כתובת משנית"
                    value={formatAddress(user.secondaryAddress)}
                />
                <DetailRow
                    label="כתובת שלישית"
                    value={formatAddress(user.tertiaryAddress)}
                />
                <DetailRow label="אזור" value={user.region} />
                <DetailRow label="תת אזור" value={user.subRegion} />
                <DetailRow
                    label="ארגון"
                    value={user.organization?.name || user.organization?._id}
                />
            </Section>

            <Section title="חשבונות והרשאות">
                {user.userAccounts?.length ? (
                    user.userAccounts.map((userAccount, index) => (
                        <AccountRow key={userAccount?._id || index}>
                            <DetailRow
                                label={`חשבון ${index + 1}`}
                                value={getAccountName(userAccount)}
                            />
                            <DetailRow
                                label="תפקיד בחשבון"
                                value={getAccountRole(userAccount)}
                            />
                        </AccountRow>
                    ))
                ) : (
                    <DetailRow label="חשבונות" value="לא נמצאו חשבונות" />
                )}
            </Section>

            <Section title="מידע נוסף">
                <DetailRow
                    label="נוצר בתאריך"
                    value={formatDate(user.created)}
                />
                <DetailRow
                    label="עודכן בתאריך"
                    value={formatDate(user.updated)}
                />
                <DetailRow
                    label="מקור הרשמה מהאפליקציה"
                    value={user.appSignup}
                />
                <DetailRow
                    label="מאפשר פניות WhatsApp"
                    value={user.allowWhatsAppInquiries}
                />
                <DetailRow
                    label="מחובר לשיווק"
                    value={user.allowMarketingEmails}
                />
                <DetailRow label="שפות" value={user.languages?.join(', ')} />
                <DetailRow
                    label="הערות"
                    value={user.comments || user.details}
                />
            </Section>
        </Stack>
    )
}

const UserDetails = () => {
    const {userId} = useParams()
    const {data: user, isFetching, isError, error} = useGetUserQuery({userId})

    return (
        <Page menuTitle="users">
            <Content>
                {isFetching && (
                    <Centered>
                        <CircularProgress />
                    </Centered>
                )}
                {isError && (
                    <Alert severity="error">
                        שגיאה בטעינת המשתמש: {error?.data?.error || 'נסה שנית'}
                    </Alert>
                )}
                {!isFetching && !isError && !user && (
                    <Alert severity="warning">המשתמש לא נמצא</Alert>
                )}
                {!isFetching && !isError && user && (
                    <UserDetailsContent user={user} />
                )}
            </Content>
        </Page>
    )
}

export default UserDetails

const Content = styled(PageContent)`
    padding: 56px 100px;
    gap: 24px;
`

const HeaderCard = styled(Card)`
    width: 100%;
`

const DetailsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
    gap: 16px 24px;
`

const Row = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`

const Label = styled(Typography)`
    color: #667085;
    font-size: 14px;
`

const Value = styled(Typography)`
    font-size: 16px;
    white-space: pre-wrap;
    word-break: break-word;
`

const AccountRow = styled.div`
    display: contents;
`

const Centered = styled.div`
    width: 100%;
    display: flex;
    justify-content: center;
    padding: 40px;
`
